import { findTemplateSectionIndex } from '../utils/nameUniqueness';
import { createPosString, getSpan, getStartPos, checkAreaOverlap, getRange, generateGridPlaceholders } from 'utils/section/gridUtils';
import type { TemplateFormsState } from '../index';
import type { LayoutType } from 'types';
import type { LocalFormItem } from 'types/agendaForm';
import { initialItem } from './addNewSection';

interface Payload {
  draggedSectionName: string;
  droppedSectionName: string;
  fromPosition: { x: string; y: string };
  toPosition: { x: string; y: string };
  layoutType: LayoutType;
  fromSubTabId?: string | null;
  toSubTabId?: string | null;
  draggedTabId?: string | null;
  droppedTabId?: string | null;
}

export const reorderSectionItem = (state: TemplateFormsState, { payload }: { payload: Payload }) => {
  const { draggedSectionName, droppedSectionName, fromPosition, toPosition, layoutType, fromSubTabId, toSubTabId, draggedTabId, droppedTabId } =
    payload;

  const fromX = getStartPos(fromPosition.x);
  const fromY = getStartPos(fromPosition.y);
  const toX = getStartPos(toPosition.x);
  const toY = getStartPos(toPosition.y);

  const sections = state[layoutType].sections;

  const getSectionData = (sectionName: string, tabId?: string | null) => {
    const sectionIndex = findTemplateSectionIndex(sections, sectionName, tabId);
    if (sectionIndex === -1) return null;
    const section = sections[sectionIndex];
    return {
      section,
      items: section.items,
      gridSizeX: section.sizeX,
      gridSizeY: section.sizeY,
    };
  };

  const sourceData = getSectionData(draggedSectionName, draggedTabId);
  const targetData = getSectionData(droppedSectionName, droppedTabId);

  if (!sourceData || !targetData) return;

  const sourceItems = sourceData.items;
  const targetItems = targetData.items;

  const fromSubTab = fromSubTabId ?? null;
  const toSubTab = toSubTabId ?? null;

  // 1. Find Source (filter by subTabId to avoid cross-subtab swaps)
  let fromIndex = sourceItems.findIndex((i) => getStartPos(i.x) === fromX && getStartPos(i.y) === fromY && (i.subTabId ?? null) === fromSubTab);
  if (fromIndex === -1)
    fromIndex = sourceItems.findIndex((i) => {
      const xRange = getRange(i.x);
      const yRange = getRange(i.y);
      return fromX >= xRange.start && fromX <= xRange.end && fromY >= yRange.start && fromY <= yRange.end && (i.subTabId ?? null) === fromSubTab;
    });
  if (fromIndex === -1) return;

  const sourceItem = sourceItems[fromIndex];
  const sourceSpanX = getSpan(sourceItem.x);
  const sourceSpanY = getSpan(sourceItem.y);

  // 2. Clamping
  const maxX = targetData.gridSizeX - sourceSpanX;
  const maxY = targetData.gridSizeY - sourceSpanY;
  const clampedToX = Math.max(0, Math.min(toX, maxX));
  const clampedToY = Math.max(0, Math.min(toY, maxY));

  // 3. Find Target (filter by subTabId to avoid cross-subtab swaps)
  let toIndex = targetItems.findIndex((i) => {
    const xRange = getRange(i.x);
    const yRange = getRange(i.y);
    return (
      clampedToX >= xRange.start &&
      clampedToX <= xRange.end &&
      clampedToY >= yRange.start &&
      clampedToY <= yRange.end &&
      (i.subTabId ?? null) === toSubTab
    );
  });
  if (toIndex === -1) {
    toIndex = targetItems.findIndex((i) => getStartPos(i.x) === clampedToX && getStartPos(i.y) === clampedToY && (i.subTabId ?? null) === toSubTab);
  }

  if (toIndex === fromIndex && draggedSectionName === droppedSectionName) {
    return;
  }

  if (toIndex === -1) return;

  const targetItem = targetItems[toIndex];

  const isTargetEmpty = !targetItem.itemGuid;

  if (isTargetEmpty) {
    // Map TemplateSectionItem to a shape compatible with LocalFormItem for overlap checking.
    // checkAreaOverlap only reads id, data.id, isEmpty, x, y — the rest is irrelevant.
    const mappedTargetItems = targetItems
      .filter((i) => (i.subTabId ?? null) === toSubTab)
      .map((i) => ({
        ...i,
        id: i.itemGuid || i.name || '',
        data: { id: i.itemGuid || i.name || '' },
        isEmpty: !i.itemGuid,
        x: i.x,
        y: i.y,
      })) as unknown as LocalFormItem[];

    const ignoredId = sourceItem.itemGuid || sourceItem.name || '';

    const overlapCheck = checkAreaOverlap(
      mappedTargetItems,
      { x: clampedToX, y: clampedToY, w: sourceSpanX, h: sourceSpanY },
      { x: targetData.gridSizeX, y: targetData.gridSizeY },
      ignoredId
    );

    if (overlapCheck.isCollision) return;
  } else {
    const targetSpanX = getSpan(targetItem.x);
    const targetSpanY = getSpan(targetItem.y);

    if (sourceSpanX !== targetSpanX || sourceSpanY !== targetSpanY) return;
  }

  // Compute new positions for the swap
  const targetSpanX = getSpan(targetItem.x);
  const targetSpanY = getSpan(targetItem.y);

  const newSourceX = createPosString(clampedToX, sourceSpanX);
  const newSourceY = createPosString(clampedToY, sourceSpanY);

  const newTargetX = createPosString(fromX, targetSpanX);
  const newTargetY = createPosString(fromY, targetSpanY);

  // Update positions
  sourceItem.x = String(newSourceX);
  sourceItem.y = String(newSourceY);

  targetItem.x = String(newTargetX);
  targetItem.y = String(newTargetY);

  // Swap subTabId values when moving across sub-tabs
  const sourceSubTabId = sourceItem.subTabId;
  sourceItem.subTabId = targetItem.subTabId;
  targetItem.subTabId = sourceSubTabId;

  // Swap the items in the arrays
  if (draggedSectionName !== droppedSectionName) {
    sourceItems[fromIndex] = targetItem;
    targetItems[toIndex] = sourceItem;
  } else {
    sourceItems[fromIndex] = targetItem;
    sourceItems[toIndex] = sourceItem;
  }

  // Regenerate placeholders for both sections to fill any gaps left by
  // multi-cell items that were moved. This ensures the grid layout stays
  // consistent regardless of item spans.
  const regenerateSection = (data: typeof sourceData) => {
    if (!data) return;
    const { section } = data;
    const realItems = section.items.filter((item) => !!item.itemGuid);
    const subTabIds = (section.subTabs ?? []).map((st) => st.id);
    section.items = generateGridPlaceholders(realItems, section.sizeX, section.sizeY, initialItem, subTabIds);
  };

  regenerateSection(sourceData);
  if (draggedSectionName !== droppedSectionName) {
    regenerateSection(targetData);
  }
};
